import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet } from '../ui/Sheet.jsx';
import { useUi } from '../../stores/ui.js';
import { useDebounced } from '../../hooks/useDebounced.js';
import { useSearchSuggestions } from '../../features/catalog/queries.js';
import { imageProps, formatMoney, formatPriceRange } from '../../lib/format.js';
import { Spinner } from '../ui/Spinner.jsx';

/**
 * Search, as a sheet rather than a page.
 *
 * It drops from the top, under the masthead, because that is where the search
 * button that opens it sits — a panel that appears at the opposite edge of the
 * screen from its own control makes the user look for it.
 *
 * Suggestions are debounced and only fire from two characters, because a request
 * per keystroke sends twenty to answer one question. Submitting goes to the full
 * results page, which is a real URL somebody can share.
 */

export const SearchOverlay = () => {
  const open = useUi((state) => state.searchOpen);
  const close = useUi((state) => state.closeSearch);
  const navigate = useNavigate();

  const [term, setTerm] = useState('');
  const debounced = useDebounced(term, 250);
  const { data: suggestions, isFetching } = useSearchSuggestions(debounced);

  useEffect(() => {
    if (!open) setTerm('');
  }, [open]);

  const go = (path) => {
    close();
    navigate(path);
  };

  const submit = (event) => {
    event.preventDefault();
    if (term.trim().length < 2) return;
    go(`/search?search=${encodeURIComponent(term.trim())}`);
  };

  const hasResults =
    (suggestions?.products?.length ?? 0) +
      (suggestions?.brands?.length ?? 0) +
      (suggestions?.categories?.length ?? 0) >
    0;

  return (
    <Sheet open={open} onClose={close} side="top" title="Search">
      <div className="mx-auto w-full max-w-3xl px-4 py-5 sm:px-6">
        <form onSubmit={submit} role="search">
          <label htmlFor="site-search" className="sr-only">
            Search products, brands, and categories
          </label>
          <div className="flex items-stretch">
            <div className="relative min-w-0 flex-1">
              <input
                id="site-search"
                type="search"
                autoFocus
                value={term}
                onChange={(event) => setTerm(event.target.value)}
                placeholder="Amber, kettlebell, 100 ml…"
                autoComplete="off"
                className="h-12 w-full border border-r-0 border-mist bg-surface pr-11 pl-4 text-sm text-ink placeholder:text-muted/70 focus:border-brand focus:outline-none"
              />
              {isFetching && (
                <Spinner className="absolute top-1/2 right-4 size-4 -translate-y-1/2 text-brand" />
              )}
            </div>
            <button
              type="submit"
              className="flex shrink-0 items-center gap-2 bg-brand px-4 text-2xs font-semibold tracking-[0.08em] text-white uppercase transition-colors hover:bg-brand-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand sm:px-6"
            >
              <svg viewBox="0 0 20 20" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <circle cx="9" cy="9" r="5.5" />
                <path d="m13.2 13.2 3.3 3.3" strokeLinecap="round" />
              </svg>
              <span className="hidden sm:inline">Search</span>
              <span className="sr-only sm:hidden">Search</span>
            </button>
          </div>
        </form>

        <div className="mt-6 max-h-[52vh] overflow-y-auto" aria-live="polite">
          {debounced.trim().length < 2 ? (
            <p className="py-6 text-center text-xs text-muted">
              Type at least two characters. Try a note, a material, or a size.
            </p>
          ) : !hasResults && !isFetching ? (
            <p className="py-6 text-center text-xs text-muted">
              Nothing matches “{debounced}”. The full catalogue is under{' '}
              <button
                type="button"
                onClick={() => go('/shop')}
                className="font-medium text-brand underline decoration-brand/40 underline-offset-4 hover:decoration-brand"
              >
                Everything
              </button>
              .
            </p>
          ) : (
            <div className="space-y-7">
              {suggestions?.products?.length > 0 && (
                <section>
                  <GroupTitle>Products</GroupTitle>
                  <ul className="mt-3 divide-y divide-mist border-y border-mist">
                    {suggestions.products.map((product) => (
                      <li key={product._id}>
                        <button
                          type="button"
                          onClick={() => go(`/product/${product.slug}`)}
                          className="group flex w-full items-center gap-4 py-3 text-left transition-colors hover:bg-cream/50 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand"
                        >
                          <span className="size-14 shrink-0 bg-cream">
                            {product.images?.[0] && (
                              <img
                                {...imageProps(product.images[0], { sizes: '56px' })}
                                alt=""
                                className="size-14 object-contain mix-blend-multiply"
                              />
                            )}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-ink transition-colors group-hover:text-brand">
                              {product.title}
                            </span>
                            <span className="mt-0.5 block text-xs font-semibold text-brand" data-numeric>
                              {product.type === 'variable'
                                ? formatPriceRange(product.priceRange, product.price)
                                : formatMoney(product.price)}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {suggestions?.categories?.length > 0 && (
                <SuggestionGroup
                  title="Categories"
                  items={suggestions.categories}
                  onPick={(item) => go(`/category/${item.slug}`)}
                />
              )}

              {suggestions?.brands?.length > 0 && (
                <SuggestionGroup
                  title="Brands"
                  items={suggestions.brands}
                  onPick={(item) => go(`/brand/${item.slug}`)}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </Sheet>
  );
};

/** The gold eyebrow every result group opens with, matching the section headings. */
const GroupTitle = ({ children }) => (
  <h3 className="text-2xs font-semibold tracking-[0.12em] text-brand uppercase">{children}</h3>
);

const SuggestionGroup = ({ title, items, onPick }) => (
  <section>
    <GroupTitle>{title}</GroupTitle>
    <ul className="mt-3 flex flex-wrap gap-2">
      {items.map((item) => (
        <li key={item._id}>
          <button
            type="button"
            onClick={() => onPick(item)}
            className="border border-mist px-3.5 py-1.5 text-xs text-ink transition-colors hover:border-brand hover:bg-sand/40 hover:text-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            {item.name}
          </button>
        </li>
      ))}
    </ul>
  </section>
);

export default SearchOverlay;
